import ENDPOINTS from "@/plugins/axios/endpoints";
import { createNamespacedHelpers } from "vuex";
import {
  PROMO_CONDITIONS,
  evaluatePromoConditions,
  isBookingPromo,
  isPromoSuccess,
  promoMessage,
  promoRulesFromResponse,
  promoTargetsFromResponse
} from "@/utils/promoCode";
const MAX_HINT_ENTRIES = 2;

export default {
  data() {
    return {
      promoChecking: false
    };
  },
  computed: {
    ...createNamespacedHelpers("Order").mapGetters(["hasPromoItem"]),
    ...createNamespacedHelpers("Config").mapGetters(["promoCodesActive"])
  },
  methods: {
    promoCurrency(value) {
      return this.$options.filters.currency(value);
    },
    // Names the promo targets so the customer knows what to buy. The promo
    // response carries the names, so nothing is resolved from the menu.
    promoTargetsHint(rules) {
      const label = list =>
        list.slice(0, MAX_HINT_ENTRIES).join(", ") +
        (list.length > MAX_HINT_ENTRIES ? ", ..." : "");

      const parts = [];
      if ((rules.itemNames || []).length) parts.push(label(rules.itemNames));
      if ((rules.itemGroupNames || []).length)
        parts.push(label(rules.itemGroupNames));

      if (!parts.length) return "";
      return (
        " " +
        this.$t("generic.lang_promoTargetsHint", { targets: parts.join(" / ") })
      );
    },
    promoConditionMessage(result, rules) {
      const code = rules.code;
      // A booking promo carries no condition the customer can fix, its item simply left the cart.
      if (isBookingPromo(rules))
        return this.$t("generic.lang_promoItemRemoved", { code });

      switch (result.reason) {
        case PROMO_CONDITIONS.BASKET_TOO_LOW:
          return this.$t("generic.lang_promoBasketTooLow", {
            amount: this.promoCurrency(result.missingAmount),
            code
          });
        case PROMO_CONDITIONS.NOT_ENOUGH_ITEMS: {
          const count = result.missingQuantity;
          const key =
            count === 1
              ? "lang_promoNotEnoughItem"
              : "lang_promoNotEnoughItems";
          return (
            this.$t("generic." + key, { count, code }) +
            this.promoTargetsHint(rules)
          );
        }
        default:
          return (
            this.$t("generic.lang_promoNoEligibleItem", { code }) +
            this.promoTargetsHint(rules)
          );
      }
    },
    promoInfo(text) {
      return this.$swal({
        title: this.$t("generic.lang_info"),
        text,
        icon: "info"
      });
    },
    promoError(text) {
      return this.$swal({
        title: this.$t("generic.lang_error"),
        text,
        icon: "error"
      });
    },
    promoSuccess(text) {
      return this.$swal({
        title: this.$t("generic.lang_success"),
        text,
        icon: "success",
        timer: 1600,
        buttons: false
      });
    },
    checkPromoCode(code) {
      return window.axios.get(ENDPOINTS.SELF_SERVICE.PROMOCODE.CHECK(code));
    },
    /**
     * Single entry point for the promo endpoint.
     * Resolves { applied, targetFlow }, targetFlow is set when the promo can
     * still be earned by booking one of its assigned items.
     */
    async redeemPromoCode(rawCode) {
      const code = String(rawCode || "")
        .trim()
        .toUpperCase();

      if (!code || this.promoChecking)
        return { applied: false, targetFlow: null };
      if (!this.promoCodesActive) return { applied: false, targetFlow: null };

      if (this.hasPromoItem) {
        await this.$swal({
          title: this.$t("generic.lang_warning"),
          text: this.$t("generic.lang_promoOnePerOrder"),
          icon: "warning"
        });
        return { applied: false, targetFlow: null };
      }

      this.promoChecking = true;
      try {
        const { data } = await this.checkPromoCode(code);

        if (!isPromoSuccess(data) || !data.promotion) {
          await this.promoError(
            promoMessage(data) || this.$t("generic.lang_promoNotValid")
          );
          return { applied: false, targetFlow: null };
        }

        const rules = promoRulesFromResponse(data.promotion, code);
        const items = this.$store.getters["Order/card"] || [];
        const result = evaluatePromoConditions(items, rules);

        if (!result.ok) {
          // Assigned items came back with the code, let the customer book them
          // instead of dead-ending on a message.
          const targets = promoTargetsFromResponse(data, rules);
          if (targets.length) {
            return { applied: false, targetFlow: { rules, targets, result } };
          }
          await this.promoInfo(
            isBookingPromo(rules)
              ? this.$t("generic.lang_promoItemUnavailable", {
                  code: rules.code
                })
              : this.promoConditionMessage(result, rules)
          );
          return { applied: false, targetFlow: null };
        }

        this.$store.commit("Order/addPromoItem", rules);
        this.promoSuccess(
          promoMessage(data) || this.$t("generic.lang_promoApplied")
        );
        return { applied: true, targetFlow: null };
      } catch (error) {
        await this.promoError(
          promoMessage(error && error.response && error.response.data) ||
            this.$t("generic.lang_promoNotValid")
        );
        return { applied: false, targetFlow: null };
      } finally {
        this.promoChecking = false;
      }
    },
    removePromoCode() {
      this.$store.commit("Order/removePromoCode");
    }
  }
};
